import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../api';
import { ReferralCode, ReferralConversionPayload } from '../../types/referral';
export interface ReferralState {
    referral: ReferralCode | null;
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    error: string | null;
}

export const fetchReferralByCode = createAsyncThunk('referral/fetchReferralByCode', async (code: string) => {
    const response = await api.get(`/referral/code/${code}`);
    return response.data.body;
});

export const createReferral = createAsyncThunk('referral/createReferral', async (referral: ReferralCode) => {
    const response = await api.post('/referral', referral);
    return response.data.body;
});

/**
 * Records an affiliate-attributed purchase against our own database.
 *
 * Intentionally has no extraReducers: this is fire-and-forget tracking called
 * from the payment success page, and it must not drive the shared status/error
 * fields that ReferralsPage renders — a tracking failure is not something to
 * show the purchaser. Callers handle rejection themselves.
 */
export const recordReferralConversion = createAsyncThunk(
    'referral/recordReferralConversion',
    async (payload: ReferralConversionPayload) => {
        const response = await api.post('/referral', payload);
        return response.data?.body ?? response.data;
    }
);

export const generateCodeByPracticeId = createAsyncThunk('referral/generateCodeByPracticeId', async (practiceId: string) => {
    const response = await api.get(`/api/referrals/generate-code/${practiceId}`);
    return response.data.body;
});

export const generateCodeByUserId = createAsyncThunk('referral/generateCodeByUserId', async (userId: string) => {
    const response = await api.get(`/api/referrals/generate-code/${userId}`);
    return response.data.body;
});

const initialState: ReferralState = {
    referral: null,
    status: 'idle',
    error: null,
};

const referralSlice = createSlice({
    name: 'referral',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(fetchReferralByCode.pending, (state) => {
            state.status = 'loading';
        });
        builder.addCase(fetchReferralByCode.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.referral = action.payload;
        });
        builder.addCase(fetchReferralByCode.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.error.message || 'Failed to fetch referral';
        });
        builder.addCase(createReferral.pending, (state) => {
            state.status = 'loading';
        });
        builder.addCase(createReferral.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.referral = action.payload;
        });
        builder.addCase(createReferral.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.error.message || 'Failed to create referral';
        });
        builder.addCase(generateCodeByPracticeId.pending, (state) => {
            state.status = 'loading';
        });
        builder.addCase(generateCodeByPracticeId.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.referral = action.payload;
        });
        builder.addCase(generateCodeByPracticeId.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.error.message || 'Failed to generate referral code by practice ID';
        });
        builder.addCase(generateCodeByUserId.pending, (state) => {
            state.status = 'loading';
        });
        builder.addCase(generateCodeByUserId.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.referral = action.payload;
        });
        builder.addCase(generateCodeByUserId.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.error.message || 'Failed to generate referral code by user ID';
        });
    },
});

export default referralSlice.reducer;
